export interface OptionButtonsOptions {
  options: Array<{ value: string; label: string }>;
  type: 'boolean' | 'single' | 'multiple';
  onSelect: (value: string | string[]) => void;
  onRendered?: () => void;
}

export class OptionButtons {
  private container: HTMLElement;
  private options: OptionButtonsOptions;
  private selectedValues: Set<string> = new Set();

  constructor(parentElement: HTMLElement, options: OptionButtonsOptions) {
    this.options = options;
    this.container = document.createElement('div');

    // Add compact class when there are many options (more than 4)
    const isCompact = options.options.length > 4;
    let className = 'options-container';
    if (options.type === 'multiple') className += ' multi-select';
    if (isCompact) className += ' compact';

    this.container.className = className;

    this.render();
    parentElement.appendChild(this.container);

    // Notify that rendering is complete
    if (options.onRendered) {
      setTimeout(() => options.onRendered?.(), 10);
    }
  }

  private render(): void {
    this.container.innerHTML = '';

    if (this.options.type === 'boolean') {
      this.renderBooleanOptions();
    } else if (this.options.type === 'single') {
      this.renderSingleOptions();
    } else {
      this.renderMultipleOptions();
    }
  }

  private renderBooleanOptions(): void {
    const yesBtn = this.createButton('Sim', 'true', 'boolean-yes');
    const noBtn = this.createButton('Não', 'false', 'boolean-no');

    yesBtn.addEventListener('click', () => {
      this.options.onSelect('true');
      this.disable();
    });

    noBtn.addEventListener('click', () => {
      this.options.onSelect('false');
      this.disable();
    });

    this.container.appendChild(yesBtn);
    this.container.appendChild(noBtn);
  }

  private renderSingleOptions(): void {
    for (const opt of this.options.options) {
      const btn = this.createButton(opt.label, opt.value);

      btn.addEventListener('click', () => {
        this.options.onSelect(opt.value);
        this.highlightSelected(btn);
        this.disable();
      });

      this.container.appendChild(btn);
    }
  }

  private renderMultipleOptions(): void {
    for (const opt of this.options.options) {
      const btn = this.createButton(opt.label, opt.value);

      btn.addEventListener('click', () => {
        if (this.selectedValues.has(opt.value)) {
          this.selectedValues.delete(opt.value);
          btn.classList.remove('selected');
        } else {
          this.selectedValues.add(opt.value);
          btn.classList.add('selected');
        }
      });

      this.container.appendChild(btn);
    }

    const confirmBtn = document.createElement('button');
    confirmBtn.className = 'btn-confirm';
    confirmBtn.textContent = 'Confirmar';
    confirmBtn.addEventListener('click', () => {
      if (this.selectedValues.size > 0) {
        this.options.onSelect(Array.from(this.selectedValues));
        this.disable();
      }
    });

    this.container.appendChild(confirmBtn);
  }

  private createButton(label: string, value: string, extraClass?: string): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.className = `option-btn${extraClass ? ` ${extraClass}` : ''}`;
    btn.textContent = label;
    btn.dataset.value = value;
    return btn;
  }

  private highlightSelected(selectedBtn: HTMLButtonElement): void {
    const buttons = this.container.querySelectorAll('.option-btn');
    buttons.forEach(btn => btn.classList.remove('selected'));
    selectedBtn.classList.add('selected');
  }

  disable(): void {
    const buttons = this.container.querySelectorAll('button');
    buttons.forEach(btn => {
      (btn as HTMLButtonElement).disabled = true;
    });
  }

  remove(): void {
    this.container.remove();
  }

  getElement(): HTMLElement {
    return this.container;
  }
}
